import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const csv = (arr) => (arr || []).join(", ");
const parse = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    body_type: "", sizes: "", favorite_colors: "", avoided_colors: "",
    preferred_styles: "", budget: "", goals: "", notes: "",
  });

  useEffect(() => {
    api.get("/profile/style")
      .then((r) => {
        const p = r.data;
        setF({
          body_type: p.body_type || "", sizes: p.sizes || "",
          favorite_colors: csv(p.favorite_colors), avoided_colors: csv(p.avoided_colors),
          preferred_styles: csv(p.preferred_styles), budget: p.budget || "",
          goals: p.goals || "", notes: p.notes || "",
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile/style", {
        body_type: f.body_type, sizes: f.sizes,
        favorite_colors: parse(f.favorite_colors), avoided_colors: parse(f.avoided_colors),
        preferred_styles: parse(f.preferred_styles), budget: f.budget,
        goals: f.goals, notes: f.notes,
      });
      toast.success("Style profile saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, placeholder) => (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} placeholder={placeholder}
        data-testid={`profile-${key}`}
        className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 text-white placeholder:text-zinc-600 transition-colors" />
    </div>
  );

  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />
      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-12">
        <div className="overline mb-3 text-volt">Personalization</div>
        <h1 className="font-display uppercase text-6xl md:text-7xl font-bold tracking-tighter mb-4">Style Profile</h1>
        <p className="text-epsub mb-10 max-w-xl">Tell Eternal your vibe. Every pairing, service and stylist reply gets tailored to this.</p>

        {loading ? (
          <div className="space-y-4">{[...Array(6)].map((_, i) => <div key={i} className="h-12 ep-loading" />)}</div>
        ) : (
          <form onSubmit={save} className="ep-card p-8 space-y-6" data-testid="profile-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {field("body_type", "Body type", "athletic, tall, curvy…")}
              {field("sizes", "Sizes", "M top, 32 waist, US 10 shoe")}
              {field("favorite_colors", "Favorite colors", "black, olive, cream")}
              {field("avoided_colors", "Colors to avoid", "yellow, orange")}
              {field("preferred_styles", "Preferred styles", "streetwear, minimalist")}
              {field("budget", "Budget", "mid / premium / thrift")}
            </div>
            <div>
              <label className="overline block mb-2">Style goals</label>
              <textarea value={f.goals} onChange={(e) => setF({ ...f, goals: e.target.value })} rows={2}
                placeholder="Look sharp for events, build a versatile everyday closet…" data-testid="profile-goals"
                className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 text-white placeholder:text-zinc-600 transition-colors resize-none" />
            </div>
            <div>
              <label className="overline block mb-2">Anything else</label>
              <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2}
                placeholder="Favorite brands, things you never wear, lifestyle…" data-testid="profile-notes"
                className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 text-white placeholder:text-zinc-600 transition-colors resize-none" />
            </div>
            <button type="submit" disabled={saving} data-testid="profile-save"
              className="ep-btn inline-flex items-center gap-2 bg-volt text-black px-8 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover disabled:opacity-60">
              {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Save size={16} /> Save Profile</>}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
